/*
 * Reminders API — backend wrapper used by every component under
 * src/components/views/reminders/. All network calls go to
 * `/v2/crm/reminder/*` on goserver, except the linked-user typeahead which
 * reuses the existing hridaya CMS endpoint `v1.0/users/search`.
 */

import { post_api, get_api } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';
import { getCookie } from '../../../util_functions';

const goUrl = (path) => get_api_url(`/v2/crm/reminder${path}`);

/* -------------------------------------------------------------------------- */
/* Enums — kept in sync with goserver/app/services/crm/constants.go           */
/* -------------------------------------------------------------------------- */

export const PRIORITIES = ['Urgent', 'High', 'Medium', 'Low'];
export const STATUSES = ['pending', 'completed'];

export const REMINDER_OFFSETS = [
  { label: 'At time of event', minutes: 0 },
  { label: '5 minutes before', minutes: 5 },
  { label: '10 minutes before', minutes: 10 },
  { label: '15 minutes before', minutes: 15 },
  { label: '30 minutes before', minutes: 30 },
  { label: '1 hour before', minutes: 60 },
  { label: '2 hours before', minutes: 120 },
  { label: '1 day before', minutes: 60 * 24 },
  { label: '2 days before', minutes: 60 * 24 * 2 },
  { label: '1 week before', minutes: 60 * 24 * 7 }
];

export const LINKED_USER_TYPES = ['trial', 'member', 'nomnot'];

/* -------------------------------------------------------------------------- */
/* In-memory caches for the synchronous *_by_uuid helpers the UI uses.        */
/* Populated as a side-effect of list_* / search_* / list_tasks calls.        */
/* -------------------------------------------------------------------------- */

const teamCache = new Map();
// Backstop for the async team list: covers a reminder already assigned to a teacher before the list loads.
const knownTeacherUuids = new Set();
const linkedCache = new Map();
const labelCache = new Map();
const departmentCache = new Map();

const cacheTeam = (rows) => {
  (rows || []).forEach((m) => teamCache.set(m.uuid, m));
};
const cacheLinked = (rows) => {
  (rows || []).forEach((u) => linkedCache.set(u.uuid, u));
};
const cacheLabels = (rows) => {
  (rows || []).forEach((l) => labelCache.set(l.uuid, l));
};
const cacheDepartments = (rows) => {
  (rows || []).forEach((d) => departmentCache.set(d.uuid, d));
};

// Staff and teachers resolve against different backend tables.
export const TEAM_KIND_TEACHER = 'teacher';

const ROLE_TEACHER = 'teacher';
const ROLE_CMS_USER = 'cms_user';

const cacheAssigneeRoles = (tasks) => {
  (tasks || []).forEach((t) => {
    ((t && t.teacher_assignee_uuids) || []).forEach((uuid) => knownTeacherUuids.add(uuid));
  });
};

// A reminder has at most one assignee, either a teacher or a Care team member.
export const task_assignee_uuid = (task) =>
  (task && (task.teacher_assignee_uuids?.[0] || task.assignee_uuids?.[0])) || '';

// The assignee's kind, read off which array it came from — lets the form carry an
// untouched assignee's role forward without re-resolving it against the team list.
export const task_assignee_kind = (task) => {
  if (!task) return '';
  if (task.teacher_assignee_uuids?.[0]) return TEAM_KIND_TEACHER;
  if (task.assignee_uuids?.[0]) return ROLE_CMS_USER;
  return '';
};

// Observers only — the assignee (teacher or staff) is read separately via task_assignee_uuid.
export const task_observer_uuids = (task) => (task && task.observer_uuids) || [];

const unwrap = (response) => (response && response.data && response.data.data) || {};

/* -------------------------------------------------------------------------- */
/* Reads                                                                       */
/* -------------------------------------------------------------------------- */

export const list_team_members = () =>
  get_api(goUrl('/team_members'), true).then((r) => {
    const list = unwrap(r).team_members || [];
    cacheTeam(list);
    return list;
  });

export const list_labels = () =>
  get_api(goUrl('/labels'), true).then((r) => {
    const list = unwrap(r).labels || [];
    cacheLabels(list);
    return list;
  });

export const list_departments = () =>
  get_api(goUrl('/departments'), true).then((r) => {
    const list = unwrap(r).departments || [];
    cacheDepartments(list);
    return list;
  });

// Sentinel assignee_uuid value that means "reminders with no assignee". Kept
// distinct from '' (which means "All team") so the Team member filter can
// offer an explicit Unassigned option.
export const UNASSIGNED_ASSIGNEE = '__unassigned__';

// Sentinel department_uuids value meaning "reminders with no department".
// Distinct from an empty selection (which means "Any department"), and from
// UNASSIGNED_ASSIGNEE so the two multi-selects can never cross-contaminate a
// persisted payload. Surfaced in the UI as "Unassigned".
//
// This exists because a NULL department_uuid can't satisfy the SQL equality the
// Depart filter used to emit, so department-less reminders (39% of pending ones
// as of 2026-08-07) were unreachable from every filtered view.
export const NO_DEPARTMENT = '__no_department__';

// Split a multi-select value that may contain a "none of them" sentinel into
// the backend's two knobs: the real uuids, and a boolean for the sentinel. The
// backend OR-combines them, so "Unassigned + Sales" returns both sets in one
// query. Shared by the assignee and department filters, which have the same
// shape server-side (`assignee_uuids`/`unassigned`, `department_uuids`/
// `no_department`).
const splitSentinelSelection = (selected, sentinel) => {
  const list = Array.isArray(selected) ? selected : [];
  return {
    uuids: list.filter((v) => v && v !== sentinel),
    wantsSentinel: list.includes(sentinel)
  };
};

// list_tasks accepts the filter shape:
//   { assignee_uuids, status, priority, linked_user_uuid, label_uuid,
//     department_uuids, due_from, due_to }
// Both the Team-member and the Depart filters are multi-select arrays that may
// mix real uuids with a "none" sentinel; see splitSentinelSelection above.
// Backward compat: legacy singular `assignee_uuid` / `department_uuid` values
// are accepted and folded in as one-element selections so older callers keep
// working. An empty selection sends neither knob → no filter on that field
// (which, for departments, deliberately still includes NULL-department rows).
export const list_tasks = (filters = {}) => {
  const payload = { ...filters };
  let selected = [];
  if (Array.isArray(payload.assignee_uuids)) {
    selected = payload.assignee_uuids;
  } else if (payload.assignee_uuid) {
    selected = [payload.assignee_uuid];
  }
  delete payload.assignee_uuid;
  const { uuids: realUuids, wantsSentinel: wantsUnassigned } = splitSentinelSelection(
    selected,
    UNASSIGNED_ASSIGNEE
  );
  if (realUuids.length > 0) payload.assignee_uuids = realUuids;
  else delete payload.assignee_uuids;
  if (wantsUnassigned) payload.unassigned = true;

  // Departments: same treatment. `department_uuid` (singular) may still arrive
  // from RemindersModal or a stale persisted payload.
  let deptSelected = [];
  if (Array.isArray(payload.department_uuids)) {
    deptSelected = payload.department_uuids;
  } else if (payload.department_uuid) {
    deptSelected = [payload.department_uuid];
  }
  delete payload.department_uuid;
  const { uuids: realDepartments, wantsSentinel: wantsNoDepartment } = splitSentinelSelection(
    deptSelected,
    NO_DEPARTMENT
  );
  if (realDepartments.length > 0) payload.department_uuids = realDepartments;
  else delete payload.department_uuids;
  if (wantsNoDepartment) payload.no_department = true;
  // "My reminders" involvement filter: pass the selected kinds through as-is
  // (the backend derives the user from the auth token). Drop it when empty so
  // no stray key is sent.
  if (Array.isArray(payload.involvement) && payload.involvement.length === 0) {
    delete payload.involvement;
  }
  // Free-text search runs server-side over every matching reminder; drop the
  // key when blank so it adds no predicate.
  if (typeof payload.q === 'string') payload.q = payload.q.trim();
  if (!payload.q) delete payload.q;
  return post_api(goUrl('/list'), payload, true).then((r) => {
    const body = unwrap(r);
    cacheLinked(body.linked_users);
    cacheAssigneeRoles(body.tasks);
    // `total` is the unpaginated match count, so callers can tell a full page
    // apart from a truncated one.
    return { tasks: body.tasks || [], total: body.total };
  });
};

export const detect_conflicts = ({ assignee_uuids = [], due_at, ignore_uuid = null }) =>
  post_api(
    goUrl('/conflicts'),
    {
      assignee_uuids: Array.isArray(assignee_uuids) ? assignee_uuids : [],
      due_at,
      ignore_uuid: ignore_uuid || ''
    },
    true
  ).then((r) => {
    const body = unwrap(r);
    cacheLinked(body.linked_users);
    return body.conflicts || [];
  });

/* -------------------------------------------------------------------------- */
/* Writes                                                                      */
/* -------------------------------------------------------------------------- */

const ingestTaskWithConflicts = (r) => {
  const body = unwrap(r);
  cacheLinked(body.linked_users);
  cacheAssigneeRoles([body.task]);
  return body.task;
};

// The form knows each person's role, so create sends a roled ref and update sends the two keys.
export const create_task = (payload /* , by_user — ignored, server uses JWT */) => {
  const { assignee_uuid, assignee_kind, observer_uuids, ...rest } = payload;
  const assignees = assignee_uuid
    ? [{ uuid: assignee_uuid, role: assignee_kind === TEAM_KIND_TEACHER ? ROLE_TEACHER : ROLE_CMS_USER }]
    : [];
  return post_api(
    goUrl('/create'),
    { ...rest, assignees, observer_uuids: observer_uuids || [] },
    true
  ).then(ingestTaskWithConflicts);
};

export const update_task = (uuid, patch /* , by_user — ignored */) => {
  const { assignee_uuid, assignee_kind, ...body } = patch;
  // Writes both keys whenever the picker was touched, clearing whichever kind
  // wasn't chosen — otherwise reassigning from a teacher to a Care member (or
  // back) would leave the old owner's uuid behind in the other field.
  if (assignee_uuid !== undefined) {
    const isTeacher = assignee_kind === TEAM_KIND_TEACHER;
    body.teacher_assignee_uuids = assignee_uuid && isTeacher ? [assignee_uuid] : [];
    body.assignee_uuids = assignee_uuid && !isTeacher ? [assignee_uuid] : [];
  }
  return post_api(goUrl('/update'), { uuid, patch: body }, true).then(ingestTaskWithConflicts);
};

export const delete_task = (uuid) =>
  post_api(goUrl('/delete'), { uuid }, true).then(() => ({ ok: true }));

export const add_note = (task_uuid, text /* , author — ignored */) =>
  post_api(goUrl('/add_note'), { task_uuid, text }, true).then((r) => unwrap(r).task);

export const update_note = (task_uuid, note_uuid, text) =>
  post_api(goUrl('/update_note'), { task_uuid, note_uuid, text }, true).then((r) => unwrap(r).task);

export const delete_note = (task_uuid, note_uuid) =>
  post_api(goUrl('/delete_note'), { task_uuid, note_uuid }, true).then((r) => unwrap(r).task);

export const create_label = (name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) return Promise.resolve(null);
  return post_api(goUrl('/labels'), { name: trimmed }, true).then((r) => {
    const { label } = unwrap(r);
    if (label) labelCache.set(label.uuid, label);
    return label;
  });
};

export const create_department = (name) => {
  const trimmed = (name || '').trim();
  if (!trimmed) return Promise.resolve(null);
  return post_api(goUrl('/departments'), { name: trimmed }, true).then((r) => {
    const { department } = unwrap(r);
    if (department) departmentCache.set(department.uuid, department);
    return department;
  });
};

// upload_attachment(file): POST multipart to the goserver upload endpoint;
// returns the persisted descriptor { key, name, type, url } that the form
// then keeps in state and echoes back in the task create/update payload.
// `key` is the durable identifier; `url` is a short-lived signed URL the
// form can use to preview the file before the reminder is even saved.
export const upload_attachment = (file) => {
  const form = new FormData();
  form.append('file', file);
  return post_api(goUrl('/upload_attachment'), form, true, 'formdata').then((r) => {
    const body = (r && r.data && r.data.data) || {};
    return body;
  });
};

/* -------------------------------------------------------------------------- */
/* Linked users (students/teachers) — typeahead via existing CMS API.         */
/* -------------------------------------------------------------------------- */

// search_linked_users(q, role) queries the existing CMS users/search endpoint
// (Erlang chat_server: server/src/user/mod_user_external.erl).
//   POST /v1.0/users/search  body: { filter, role, limit, offset }
//   role: 'student' | 'teacher'
//   response: { users: [{ uuid, fname, lname, email, avatar }] }
export const search_linked_users = (q, role) => {
  const filter = (q || '').trim();
  if (filter.length < 3) return Promise.resolve([]);
  const url = get_api_url('v1.0/users/search', false, false, true);
  return post_api(url, { filter, role, limit: 100, offset: 0 }, true).then((r) => {
    const body = (r && r.data) || {};
    const rows = body.users || [];
    const normalised = rows.map((u) => ({
      uuid: u.uuid,
      name: `${u.fname || ''} ${u.lname || ''}`.trim(),
      role,
      email: u.email,
      avatar: u.avatar
    }));
    cacheLinked(normalised);
    return normalised;
  });
};

// list_linked_users() — kept for compatibility but returns whatever is in the
// cache so far. Real searching should go through search_linked_users(q, role).
export const list_linked_users = () => Promise.resolve(Array.from(linkedCache.values()));

/* -------------------------------------------------------------------------- */
/* Sync lookups — used by render fns; resolve out of the in-memory caches.    */
/* -------------------------------------------------------------------------- */

export const team_member_by_uuid = (uuid) => teamCache.get(uuid) || null;
export const linked_user_by_uuid = (uuid) => linkedCache.get(uuid) || null;
export const label_by_uuid = (uuid) => labelCache.get(uuid) || null;
export const department_by_uuid = (uuid) => departmentCache.get(uuid) || null;
export const get_linked_user = (uuid) => Promise.resolve(linkedCache.get(uuid) || null);

// current_team_member(cms_email) returns the cached team member matching the
// CMS email. The cache is primed on module load (below), so this is reliable
// shortly after the first page paint. Returns null if the cache hasn't filled
// yet — components already guard with `me?.uuid`.
export const current_team_member = (cms_email) => {
  const email = cms_email || getCookie('cms_email');
  if (!email) return null;
  for (const m of teamCache.values()) {
    if (m.email === email) return m;
  }
  return null;
};

// Cache-prime helper. We deliberately do NOT call this at module load time —
// this file is imported transitively from student/teacher profile pages and
// from Home, so a top-level fetch would fire on pages that never use
// reminders (and possibly before auth cookies are ready). Components that
// surface reminders UI (RemindersModal, the dashboard) call this from their
// own mount effects instead.
export const prime_team_members = () => {
  list_team_members().catch(() => {
    /* swallow — components handle empty cache */
  });
};
